import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import { OrderModel } from '@epicuro-next/data/order-book';
import { SimpleSelectOptionType } from '@epicuro-next/features/landing-panel';
import {
  IDynamicBindableValueCombination,
  IDynamicBindableValue,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { Order } from 'graphql-api/gql-api-interfaces';
import { createInstance } from '../../../../utils/dynamic-items-utils';
import { ShipToAddressType } from '../../../types/ship-to-address';
import { daysIntoTheFutureProvider } from './products-on-the-way-days-into-future-provider';

type HttpOrdersProviderType = {
  filters: [
    {
      fieldId: 'DeliveryDate_requested';
      values: [string, string];
    },
  ];
  search: '';
};
export const httpOrdersProvider: IDynamicBindableValue = {
  provider: 'HttpOrderService',
  property: {
    values: [
      { provider: 'id:square-panel-root', property: 'daysIntoThePast' },
      daysIntoTheFutureProvider,
    ],
    transform: function ([daysIntoThePast, daysIntoTheFuture]: [
      number | string,
      number | string,
    ]): HttpOrdersProviderType {
      const currentDate = new Date();

      const prevDate = new Date(
        new Date().setDate(currentDate.getDate() - Number(daysIntoThePast)),
      );

      const nextDate = new Date(
        new Date().setDate(currentDate.getDate() + Number(daysIntoTheFuture)),
      );

      function formatDateToIsoString(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        const formattedDate = `${year}-${month}-${day}`;
        return formattedDate;
      }

      const previousDateStr = formatDateToIsoString(prevDate);
      const nextDateStr = formatDateToIsoString(nextDate);

      return {
        filters: [
          {
            fieldId: 'DeliveryDate_requested',
            values: [previousDateStr, nextDateStr],
          },
        ],
        search: '',
      };
    }.toString(),
  },
  filter: function (value: unknown): boolean {
    return value !== undefined;
  }.toString(),
};

export const shipToOptionsProvider: IDynamicBindableValueCombination = {
  values: [
    recommendationsPanelsLocaleKeys.allShops as unknown as IDynamicBindableValue,
    httpOrdersProvider,
    {
      provider: 'GlobalStoreProviderService',
      property: {
        path: 'accounts.activeAccount.shipToAddresses',
      },
      filter: function (value: unknown): boolean {
        return value !== undefined;
      }.toString(),
    },
  ],
  transform: function ([defaultTextValue, ordersData, shipToAddressess]: [
    string,
    OrderModel,
    ShipToAddressType[],
  ]): SimpleSelectOptionType[] {
    function calcProductCount(address: string): number {
      return ordersData.orders.reduce((prev: number, curr: Order) => {
        if (
          curr?.address?.split('(')?.pop()?.includes(address) &&
          curr?.confirmedQuantity
        ) {
          return prev + curr.confirmedQuantity;
        }

        return prev;
      }, 0);
    }

    const shipToAddressFilters =
      shipToAddressess?.map((item: ShipToAddressType) => ({
        count: calcProductCount(item.realAddressId ?? ''),
        displayName: item.name,
        realAddressId: item.realAddressId,
      })) ?? [];

    const selectOptions: SimpleSelectOptionType[] = [
      {
        displayValue: defaultTextValue,
        value: undefined as unknown as string,
      },
    ];

    for (const shipToAddressFilter of shipToAddressFilters) {
      const quantity = shipToAddressFilter?.count ?? 0;

      const shopName = shipToAddressFilter.displayName;
      const displayValue = `${quantity} | ${shopName} (${shipToAddressFilter.realAddressId})`;

      const filterValue: SimpleSelectOptionType = {
        value: shipToAddressFilter.realAddressId ?? '',
        displayValue,
        icon: 'truck',
      };

      selectOptions.push(filterValue);
    }

    return selectOptions;
  }.toString(),
};

export const simpleShipToSelect = createInstance({
  id: 'filter-by-ship-to-select',
  styles: {
    style: {
      display: 'block',
      'max-height': '400px',
      'overflow-y': 'auto',
    },
  },
  contentTypeId: 'dynamic-elements-simple-select',
  inputs: {
    options: shipToOptionsProvider,
    selectedValue: {
      id: 'square-panel-root',
      property: 'selectedShipTo',
    },
  },
});

const storeText: IDynamicSimpleText = {
  id: 'products-on-the-way-stores-part-of-text',
  contentTypeId: 'dynamic-elements-simple-text',
  styles: {
    style: { 'white-space': 'pre', 'text-decoration': 'underline' },
    classes: ['cursor-pointer'],
    variables: {
      '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
    },
  },
  inputs: {
    text: '[stores]',
  },
};

export const StoresDropdown = createInstance({
  contentTypeId: 'dynamic-elements-dropdown-wrapper',
  inputs: {
    triggerContent: storeText,
    dropdownContent: simpleShipToSelect,
  },
});
